// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

import { ApiRequestError, api, type CloudPointsQuery, type RuxApiClient } from '../api/client';
import type { CloudTileIndex, TileInfo } from '../api/types';
import { isRuxp, parseRuxp } from './binaryPoints';
import { visibleTileIds } from './frustumCull';
import { pageCount, pageIsLod, pageTotal, toPageBuffers, type NamedLabelPage, type StreamPage } from './decode';
import { computeTileFraction, desiredCount } from './tileLevel';
import {
  DEFAULT_OVERVIEW_POINTS,
  DEFAULT_PAGE_SIZE,
  clampOverviewPoints,
  clampPageSize,
  loadFraction,
  planPages,
} from './pagination';
import type { PageBuffers } from './PointCloudScene';
import { cachedFetch, cachedValue, pageBytes, queryKey, streamKey, viewerCache } from './viewerCache';

export interface CloudStreamState {
  /** Points handed to `onPage` as full-resolution pages so far. */
  loaded: number;
  /**
   * Points in the coarse overview, once it has been applied.
   *
   * Deliberately not folded into `loaded`: the overview is a view of the whole
   * cloud, so adding it to a count that is compared against `total` would
   * report progress the stream has not made.
   */
  overview?: number;
  /** Total points in the cloud; undefined until the first page comes back. */
  total?: number;
  /** `loaded / total`, or null while the total is unknown. */
  fraction: number | null;
  done: boolean;
  error?: Error;
}

/** Which of the two things a page is, when it reaches `onPage`. */
export interface PageKind {
  /**
   * True for the coarse whole-scene page fetched before paging starts. The
   * caller must keep it in its own scene layer: it covers the same volume the
   * full-resolution pages are about to cover, and it is dropped once they have.
   */
  overview: boolean;
}

export interface CloudStreamOptions {
  /** Geometry cloud to stream. Nothing is fetched while this is undefined. */
  cloud?: string;
  /**
   * A `Label` cloud to zip against the geometry, or null for none.
   *
   * The contract guarantees sibling clouds of one scan are returned in storage
   * order and are index-aligned, so the same `offset`/`limit` window can be
   * pulled from both and joined positionally. That guarantee is the only reason
   * this works without a join key — and it is why the overview pass names the
   * geometry cloud as the label cloud's `lodSource`, so the two subsamples
   * pick the same point indices instead of two unrelated ones.
   */
  labelCloud?: string | null;
  /**
   * Several `Label` clouds to zip at once (the Ground: `instances` and
   * `rooms`), each fetched over the same window or selection and landing in
   * `PageBuffers.labelsBy[name]`; the first is also `PageBuffers.labels`.
   * Takes precedence over `labelCloud`, which stays as the one-cloud form.
   */
  labelClouds?: readonly string[];
  pageSize?: number;
  /**
   * Bumped when `cloud` or a label cloud was rewritten on the server
   * (`clouds.changed`); a change restarts the stream from scratch.
   */
  revision?: number;
  /**
   * Budget for the coarse whole-scene overview fetched first (#320). `0`
   * disables it and restores the plain prefix-paging behaviour.
   */
  overviewPoints?: number;
  /**
   * Stop after the overview: the LOD answer (`overviewPoints` budget) is
   * applied as an ordinary page and the stream is done — the Ground's Tæthed
   * below 100 %. Skips the tiled path. Against a server that ignores
   * `max_points` the stream pages as usual (there is no smaller cloud to get).
   */
  lodOnly?: boolean;
  /**
   * What the server's list says these clouds are (`viewerCache.cloudIdentity`),
   * for the cache key only: read when a stream starts, never restarts one.
   */
  identity?: string;
  /** Called once per decoded page, in page order. */
  onPage: (buffers: PageBuffers, kind: PageKind) => void;
  /**
   * Called once the full-resolution stream has covered everything the overview
   * showed, so the caller can drop the overview layer. Not called when there
   * was no overview.
   */
  onOverviewSuperseded?: () => void;
  /** Called once when every page has been applied. */
  onComplete?: () => void;
  /**
   * Camera accessor for tile-based (frustum-culled) streaming (#395).
   *
   * When provided **and** the cloud carries a spatial tile index, the stream
   * fetches only the tiles whose bbox intersects the frustum, nearest first,
   * and re-evaluates on camera movement. Without it — or for a cloud with no
   * tile index — the stream falls back to the sequential overview + paging path.
   *
   * `origin` is the recentring offset subtracted from world positions before
   * upload; the frustum is in that scene-local space, so the tile AABBs (world
   * space) are shifted by it before the intersection test.
   */
  getCameraState?: () => {
    frustum: THREE.Frustum;
    position: THREE.Vector3;
    origin: THREE.Vector3 | null;
  };
}

/** How many times a page is retried when the server answers 503. */
const RETRY_LIMIT = 3;

/** The point-page routes a stream uses. Narrow so a test can fake it. */
export type PointsClient = Pick<
  RuxApiClient,
  'cloudPoints' | 'cloudPointsBinary' | 'cloudTiles'
>;

export interface PageFetcherOptions {
  /** Defaults to the app client. */
  client?: PointsClient;
  signal?: AbortSignal;
  /** True once the stream was torn down; stops the retry loop rethrowing forever. */
  isCancelled?: () => boolean;
  /** Injected so a test does not sit on a real timer. */
  delay?: (ms: number) => Promise<void>;
}

/**
 * Fetch one page of a named cloud, in the best format the server supports.
 *
 * The query is passed as an object rather than positional `offset`/`limit`
 * because the endpoint now has two mutually exclusive modes — a window
 * (`offset`/`limit`) or a whole-cloud LOD (`maxPoints`) — and a positional
 * signature would have to encode "not this one" as a pair of `undefined`s.
 */
export type FetchPage = (name: string, query: CloudPointsQuery) => Promise<StreamPage>;

/**
 * A page fetcher with **sticky** format negotiation, for the life of one stream.
 *
 * `format=binary` is tried first (`docs/gui/binary-points.md`). A server that
 * predates #283 answers 501, and one that ignores the parameter altogether
 * answers 200 with a JSON body — both mean "this server has no RUXP", so the
 * fetcher latches to JSON and every later page in the stream goes there
 * directly. Retrying binary per page would cost a wasted round trip on each of
 * the two hundred pages a large scan takes.
 *
 * A body that *does* start with the RUXP magic but then fails to parse is a
 * different animal: the server claims to speak the format and does not. That
 * propagates to `state.error` and reaches the user, because silently falling
 * back would turn a server bug into a permanent, invisible slow path.
 *
 * A 503 is retried with backoff rather than surfaced: it means a running job
 * momentarily held the database, which is a normal condition in this app and
 * not something to make the user re-click.
 *
 * Exported separately from the hook so the negotiation can be tested without a
 * DOM, a canvas or a React renderer.
 */
export function createPageFetcher(options: PageFetcherOptions = {}): FetchPage {
  const client = options.client ?? api;
  const { signal, isCancelled = () => false, delay = sleep } = options;

  // The latch. Starts optimistic; only ever moves binary -> json.
  let binarySupported = true;

  const fetchOnce = async (name: string, query: CloudPointsQuery): Promise<StreamPage> => {
    if (binarySupported) {
      let buffer: ArrayBuffer;
      try {
        buffer = await client.cloudPointsBinary(name, query, signal);
      } catch (cause) {
        // 503 is transient and must reach the retry loop unchanged; only a
        // "this server cannot do binary" answer flips the latch.
        if (!(cause instanceof ApiRequestError) || !cause.isNotImplemented) throw cause;
        binarySupported = false;
        return fetchOnce(name, query);
      }
      if (!isRuxp(buffer)) {
        binarySupported = false;
        return fetchOnce(name, query);
      }
      return { format: 'binary', page: parseRuxp(buffer) };
    }
    return { format: 'json', page: await client.cloudPoints(name, query, signal) };
  };

  return async (name, query) => {
    for (let attempt = 0; ; attempt += 1) {
      try {
        return await fetchOnce(name, query);
      } catch (cause) {
        if (isCancelled()) throw cause;
        const retryable = cause instanceof ApiRequestError && cause.isRetryable;
        if (!retryable || attempt >= RETRY_LIMIT) throw cause;
        await delay(150 * 2 ** attempt);
      }
    }
  };
}

/**
 * Walk a cloud, coarsely first and then page by page, handing each decoded page
 * to `onPage`.
 *
 * ## The overview pass (#320)
 *
 * The first request is not the first page — it is `max_points`, a voxel
 * subsample of the **whole** cloud. A viewport does not want the first 100 000
 * points of a 10-million-point scan; it wants all ten million of them, coarsely,
 * and then finer. One request now puts the entire scan on screen, correctly
 * framed, before any of the ~100 paging requests behind it have returned.
 *
 * It doubles as the "how big is this cloud" probe, so it costs no extra round
 * trip. Three answers are possible and all three are handled:
 *
 * - **A LOD page** (`lod` set): the scan is bigger than the budget. Apply it as
 *   the overview, then page the cloud properly and drop the overview at the end.
 * - **The whole cloud** (`lod` clear, `count === total`): it fit the budget, so
 *   this *is* the cloud. Apply it as an ordinary page and stop — one request
 *   for a small cloud, where the old code always made at least one more.
 * - **A prefix** (`lod` clear, `count < total`): a server that predates
 *   `max_points` ignored it. Fall through to plain paging from offset 0. One
 *   wasted request against an old server, which is the same trade the RUXP
 *   fallback already makes.
 *
 * Pages are fetched **sequentially**, not in parallel — but no longer because
 * the server cannot afford otherwise. Since #283 a page reads only the blob
 * bytes that page needs, so peak server memory is `O(page)` and a parallel
 * fetch would be affordable. Sequential stays for two reasons that outlast that
 * change: what the user actually wants is the *first* page fast, and sequential
 * already gives it — the cloud builds up in front of them instead of appearing
 * all at once at the end — and `ruxd --local` opens a fresh `ProjectDB` per request,
 * so concurrent readers only widen the window in which a running job's writer
 * turns one of them into a `SQLITE_BUSY`.
 */
export function useCloudStream(options: CloudStreamOptions): CloudStreamState {
  const {
    cloud,
    labelCloud = null,
    labelClouds,
    revision = 0,
    pageSize = DEFAULT_PAGE_SIZE,
    overviewPoints = DEFAULT_OVERVIEW_POINTS,
    lodOnly = false,
  } = options;
  // A string key, so a caller passing a fresh array literal every render
  // cannot restart the stream. Cloud names are SQL identifiers: no `|`.
  const labelKey = (labelClouds ?? (labelCloud ? [labelCloud] : [])).join('|');

  const [state, setState] = useState<CloudStreamState>({
    loaded: 0,
    fraction: null,
    done: false,
  });

  // Callbacks are inline closures at the call site; a ref keeps them out of the
  // effect's dependencies so a parent re-render cannot restart the download.
  const onPageRef = useRef(options.onPage);
  onPageRef.current = options.onPage;
  const onCompleteRef = useRef(options.onComplete);
  onCompleteRef.current = options.onComplete;
  const onSupersededRef = useRef(options.onOverviewSuperseded);
  onSupersededRef.current = options.onOverviewSuperseded;
  const getCameraStateRef = useRef(options.getCameraState);
  getCameraStateRef.current = options.getCameraState;
  const identityRef = useRef(options.identity);
  identityRef.current = options.identity;

  useEffect(() => {
    if (!cloud) {
      setState({ loaded: 0, fraction: null, done: true });
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    const limit = clampPageSize(pageSize);
    const overviewBudget = clampOverviewPoints(overviewPoints);
    const client = api;
    // Set by the tiled path so the effect cleanup can drop its camera listener.
    let cameraMoveCleanup: (() => void) | undefined;

    const labelNames = labelKey ? labelKey.split('|') : [];

    // Every response is kept per case (`viewerCache.ts`, frontend-38f), keyed
    // by this stream's identity and the exact query: a remount — the Ground
    // after a round trip through an expert page — replays the same requests
    // from memory instead of the network. `revision` is part of the key, so
    // a rewritten cloud never serves its old pages.
    const scope = api.caseId ?? null;
    const stream = streamKey(cloud, labelKey, revision, identityRef.current ?? '');
    const tags = [cloud, ...labelNames];
    const fetchPage: FetchPage = cachedFetch(
      viewerCache(),
      scope,
      stream,
      tags,
      // One fetcher per stream, so the format latch is per stream too: a
      // reload against a restarted server tries binary again.
      createPageFetcher({
        signal: controller.signal,
        isCancelled: () => cancelled,
      }),
      queryKey,
      pageBytes,
    );

    const applyPage = (
      geometry: StreamPage,
      labels: NamedLabelPage[],
      kind: PageKind = { overview: false },
    ) => {
      const buffers = toPageBuffers(geometry, labels);
      if (!buffers) return 0;
      onPageRef.current(buffers, kind);
      return pageCount(geometry);
    };

    /**
     * The label siblings of one geometry request, in order. `lodSource` is
     * what keeps them index-aligned under `max_points` or `tile`: a label
     * cloud has no positions of its own to voxelise or tile, and a second,
     * independent selection would zip the wrong labels onto the points.
     */
    const fetchLabels = async (query: CloudPointsQuery): Promise<NamedLabelPage[]> => {
      const out: NamedLabelPage[] = [];
      for (const name of labelNames) {
        if (cancelled) break;
        const needsSource = query.maxPoints !== undefined || query.tile !== undefined;
        out.push({ name, page: await fetchPage(name, { ...query, ...(needsSource ? { lodSource: cloud } : {}) }) });
      }
      return out;
    };

    /** Geometry plus its label siblings, over the same window or selection. */
    const fetchBoth = async (query: CloudPointsQuery) => {
      const geometry = await fetchPage(cloud, query);
      if (cancelled || labelNames.length === 0) return { geometry, labels: [] as NamedLabelPage[] };
      return { geometry, labels: await fetchLabels(query) };
    };

    /**
     * Frustum-culled, multi-level tile streaming (#395 / #396).
     *
     * Returns true when it handled the cloud (tile index present and a camera
     * accessor was given); false to signal the caller to fall through to the
     * sequential overview + paging path.
     *
     * Each visible tile is fetched at a LOD fraction determined by its angular
     * size (see `tileLevel.ts`). On camera settle the fraction is re-evaluated
     * and only the delta slice is requested — already-fetched points are never
     * re-fetched. Tiles that become invisible while partially loaded are left
     * as-is; when they come back into view the next camera-settle pass fetches
     * the remaining fraction if it has increased.
     */
    const runTiled = async (): Promise<boolean> => {
      const getCameraState = getCameraStateRef.current;
      if (!getCameraState) return false;

      // No tile index for this cloud (or an old server) is an answer too,
      // kept as null so a replay does not ask again: page it the old way.
      const index = await cachedValue<CloudTileIndex | null>(
        viewerCache(),
        scope,
        `${stream}\u0000tiles`,
        tags,
        () =>
          client.cloudTiles(cloud, controller.signal).catch((cause: unknown) => {
            if (cause instanceof ApiRequestError && cause.isNotFound) return null;
            throw cause;
          }),
        (t) => 64 + (t?.tiles.length ?? 0) * 128,
      );
      if (index === null) return false;
      if (cancelled) return true;

      const total = index.point_count;
      // Per-tile fetched count. A tile is "fully loaded" when fetchedCounts[id]
      // >= tile.count. The map is never decremented — LOD only refines.
      const fetchedCounts = new Map<number, number>();
      let loaded = 0;

      // Build a map from tile id to TileInfo for O(1) lookup.
      const tileById = new Map<number, TileInfo>();
      for (const tile of index.tiles) tileById.set(tile.id, tile);

      /**
       * Fetch the next slice of tile `id`, from `offset` to `offset + limit`.
       * Applies the resulting page and updates the per-tile and total counts.
       */
      const fetchTileSlice = async (id: number, offset: number, limit: number) => {
        const query: CloudPointsQuery = { tile: id, offset, limit };
        const geometry = await fetchPage(cloud, query);
        if (cancelled) return;
        // Label clouds have no positions, so tile membership cannot be
        // computed on them directly: `fetchLabels` adds `lodSource` and passes
        // the same offset/limit, so the server applies the identical
        // skip/limit to the geometry-derived indices and they stay aligned.
        const labels = await fetchLabels(query);
        if (cancelled) return;
        const n = applyPage(geometry, labels);
        loaded += n;
        fetchedCounts.set(id, (fetchedCounts.get(id) ?? 0) + n);
        setState({ loaded, total, fraction: loadFraction(loaded, total), done: false });
      };

      /**
       * Evaluate the visible tile set and fetch any outstanding slices.
       *
       * For each visible tile, the desired point count is computed from its
       * angular size. If the tile has not yet reached that count, the delta
       * slice is requested. Already-complete tiles are skipped without a
       * network call.
       */
      const fetchVisibleLod = async () => {
        const { frustum, position, origin } = getCameraState();
        const ids = visibleTileIds(index.tiles, frustum, position, origin);
        for (const id of ids) {
          if (cancelled) return;
          const tile = tileById.get(id);
          if (!tile || tile.count === 0) continue;

          const fraction = computeTileFraction(tile, position, origin);
          const desired = desiredCount(tile.count, fraction);
          const alreadyFetched = fetchedCounts.get(id) ?? 0;
          if (alreadyFetched >= desired) continue;

          const delta = desired - alreadyFetched;
          await fetchTileSlice(id, alreadyFetched, delta);
        }
      };

      setState({ loaded: 0, total, fraction: loadFraction(0, total), done: false });
      await fetchVisibleLod();
      if (cancelled) return true;

      // Re-evaluate whenever the camera settles. The debounce timer matches
      // #395's 150 ms and re-uses the same `rux-camera-move` event.
      let timer: ReturnType<typeof setTimeout> | undefined;
      const onMove = () => {
        if (timer !== undefined) clearTimeout(timer);
        timer = setTimeout(() => {
          fetchVisibleLod().catch(() => {
            /* a failed refresh is not fatal to the stream */
          });
        }, 150);
      };
      if (typeof window !== 'undefined')
        window.addEventListener('rux-camera-move', onMove);
      cameraMoveCleanup = () => {
        if (timer !== undefined) clearTimeout(timer);
        if (typeof window !== 'undefined')
          window.removeEventListener('rux-camera-move', onMove);
      };

      setState({ loaded, total, fraction: loadFraction(loaded, total), done: true });
      onCompleteRef.current?.();
      return true;
    };

    const run = async () => {
      setState({ loaded: 0, fraction: null, done: false });

      const coarseOnly = lodOnly && overviewBudget > 0;
      if (!coarseOnly && (await runTiled())) return;

      let total: number | undefined;
      let overview = 0;

      if (overviewBudget > 0) {
        const first = await fetchBoth({ maxPoints: overviewBudget });
        if (cancelled) return;
        total = pageTotal(first.geometry);

        const whole = !pageIsLod(first.geometry) && pageCount(first.geometry) >= total;
        if (whole || (coarseOnly && pageIsLod(first.geometry))) {
          // Either the whole cloud fit the budget — it is already on screen at
          // full resolution, and paging it again would refetch the same
          // points — or the caller asked for the coarse view only. Either way
          // this page is the layer, not an overview to be dropped later.
          const loaded = applyPage(first.geometry, first.labels);
          setState({ loaded, total, fraction: loadFraction(loaded, total), done: true });
          onCompleteRef.current?.();
          return;
        }

        if (pageIsLod(first.geometry)) {
          overview = applyPage(first.geometry, first.labels, { overview: true });
          setState({ loaded: 0, overview, total, fraction: loadFraction(0, total), done: false });
        }
        // Otherwise the server ignored `max_points`; fall through and page.
      }

      let loaded = 0;
      let applied = 0;

      /** One full-resolution page. False means the stream was torn down. */
      const pageAt = async (offset: number, pageLimit: number) => {
        const page = await fetchBoth({ offset, limit: pageLimit });
        if (cancelled) return false;
        if (total === undefined) total = pageTotal(page.geometry);
        loaded += applyPage(page.geometry, page.labels);
        applied += 1;
        setState({ loaded, overview, total, fraction: loadFraction(loaded, total), done: false });
        return true;
      };

      // Without a usable overview the first page is the size probe, exactly as
      // it was before #320 — so a `overviewPoints: 0` stream behaves
      // identically to the old one, request for request.
      let firstPlan = 0;
      if (total === undefined) {
        if (!(await pageAt(0, limit))) return;
        firstPlan = 1;
      }

      for (const plan of planPages(total ?? 0, limit).slice(firstPlan)) {
        if (cancelled) return;
        if (!(await pageAt(plan.offset, plan.limit))) return;
      }

      if (cancelled) return;
      if (overview > 0 && applied > 0) onSupersededRef.current?.();
      setState({
        loaded,
        overview,
        total,
        fraction: loadFraction(loaded, total),
        done: true,
      });
      onCompleteRef.current?.();
    };

    // `overviewPoints` is a number, so a caller passing a literal cannot
    // restart the stream by re-rendering — same reason the callbacks are refs.
    run().catch((cause: unknown) => {
      if (cancelled || controller.signal.aborted) return;
      setState((current) => ({
        ...current,
        done: true,
        error: cause instanceof Error ? cause : new Error(String(cause)),
      }));
    });

    return () => {
      cancelled = true;
      controller.abort();
      cameraMoveCleanup?.();
    };
  }, [cloud, labelKey, revision, pageSize, overviewPoints, lodOnly]);

  return state;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
